import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";

const links = [
    { to: "/champagne-and-bagel-bites", label: "Champagne & Bagel Bites" },
    { to: "/overachieving-for-me", label: "Overachieving for Me" },
    { to: "/life-as-an-elective", label: "Life as an Elective" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
];

export default function Nav() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => setOpen(false), [location.pathname]);

    return (
        <header
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "backdrop-blur-md bg-[rgb(var(--paper))]/90 border-b hairline"
                    : "bg-[rgb(var(--paper))] border-b border-transparent"
            }`}
            data-testid="site-nav"
        >
            <div className="mx-auto max-w-[1280px] px-6 md:px-10 py-4 md:py-5 flex items-center justify-between">
                <Link to="/" className="wordmark text-lg" data-testid="nav-brand">
                    Overachieving <em>for me</em>
                </Link>

                <nav className="hidden lg:flex items-center gap-8">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                            data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <button
                    onClick={() => setOpen((v) => !v)}
                    className="lg:hidden p-2 -mr-2 text-[rgb(var(--teal))]"
                    aria-label="Open menu"
                    data-testid="nav-menu-toggle"
                >
                    {open ? <X size={20} /> : <Menu size={20} />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:hidden overflow-hidden bg-[rgb(var(--paper))] border-t hairline"
                    >
                        <div className="px-6 py-6 flex flex-col gap-4">
                            {links.map((l) => (
                                <NavLink
                                    key={l.to}
                                    to={l.to}
                                    className={({ isActive }) =>
                                        `text-base ${isActive ? "text-[rgb(var(--orange))]" : "text-[rgb(var(--teal))]"} font-semibold tracking-wide uppercase`
                                    }
                                    data-testid={`mnav-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                >
                                    {l.label}
                                </NavLink>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
